import { CommonModule, DOCUMENT, isPlatformBrowser, NgClass, NgStyle, registerLocaleData } from "@angular/common";
import es from "@angular/common/locales/es";
import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from "@angular/core";
import { MetaDefinition, Title } from "@angular/platform-browser";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { environment } from "@src/environments/environment";
import { GoogleAnalyticsService } from "ngx-google-analytics";
import { firstValueFrom, Observable, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { CookieConsentSubComponent } from "./componentes/cookie-consent/cookie-consent-sub.component";
import { CookieConsentComponent } from "./componentes/cookie-consent/cookie-consent.component";
import { FooterComponent } from "./componentes/footer/footer.component";
import { HeaderComponent } from "./componentes/header/header.component";
import { SafeResourceUrlPipe } from "./pipes/safeResourceUrl/safe-resource-url.pipe";
import { CanonicalService } from "./servicios/canonical/canonical.service";
import { ConfigService } from "./servicios/config/config.service";
import { ConsentService, consentType } from "./servicios/consent/consent.service";
import { DeviceService } from "./servicios/device/device.service";
import { FirebaseService } from "./servicios/firebase.service";
import { FooterService } from "./servicios/footer.service";
import { IframeService } from "./servicios/iframe/iframe.service";
import { NoticiasService } from "./servicios/noticias/noticias.service";
import { PerformanceTrackingService } from "./servicios/PerformanceTrackingService";
import { PlatformService } from "./servicios/platform/platform.service";
import { PortadaService } from "./servicios/portada/portada.service";
import { PublicidadService } from "./servicios/publicidad/publicidad.service";
import { SeoService } from "./servicios/seo/seo.service";
import { SidenavService } from "./servicios/sidenav/sidenav.service";

export type consents = {
  init: boolean;
  config: boolean;
  rendimiento: boolean;
  publicidad: boolean;
};

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [CommonModule, HeaderComponent, NgStyle, NgClass, RouterOutlet, FooterComponent, SafeResourceUrlPipe, CookieConsentSubComponent, CookieConsentComponent],
  providers: [IframeService, SeoService, SidenavService, PortadaService, NoticiasService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  constantesGlobales: any;
  isShow = true;
  flotante_footer = true;
  analyticCreated = false;
  isInIframe = false;
  navigationSub: Subscription;
  titleSub: Subscription;
  routerSub: Subscription;

  showFooter = true;
  isMobile = false;
  isWebView = false;
  publicidadSubscribe: Subscription;
  publicidadFondoCompleto: any;
  publicidadCabecera: any;
  publicidadIzquierda: any;
  publicidadIzquierdaAbajo: any;
  publicidadDerecha: any;
  publicidadDerechaAbajo: any;
  publicidadFooter: any;
  publicidadFooterMovil: any;

  publicidadInstertsticial: any;
  publicidadInstertsticialMovil: any;

  consentType: consentType = false;
  consents: consents = {
    init: false,
    config: false,
    publicidad: false,
    rendimiento: false,
  };
  consentsSub: Subscription;
  mostrarConsent: boolean = false;

  mostrarPublicidadIntersticial = false;
  mostrarPublicidadIntersticialMovil = false;
  temporizadorCierreIntersticial = 0;
  pageReels = false;

  private scrollEventFired = false;

  constructor(
    private readonly router: Router,
    private readonly renderer2: Renderer2,
    private readonly iframeService: IframeService,
    private readonly platformService: PlatformService,
    private readonly publicidadService: PublicidadService,
    private readonly noticiasService: NoticiasService,
    private readonly elementRef: ElementRef,
    private readonly seoService: SeoService,
    private readonly configService: ConfigService,
    private readonly consentService: ConsentService,
    private readonly footerService: FooterService,
    private readonly gaService: GoogleAnalyticsService,
    private readonly titleService: Title,
    // private swPush: SwPush,
    private firebaseService: FirebaseService,
    private deviceService: DeviceService,
    @Inject(PLATFORM_ID) private platformId: object,
    private performanceTrackingService: PerformanceTrackingService,
    private readonly canonicalService: CanonicalService,
    private readonly portadaService: PortadaService,
    @Inject(DOCUMENT) private readonly document: any,
  ) {
    this.constantesGlobales = this.configService.getConstantesGlobales();
    if (isPlatformBrowser(platformId) && this.deviceService.isMobile()) {
      (async () => {
        const isMobile = await firstValueFrom(this.deviceService.isMobile());
        this.deviceService.deviceIsMobile.set(isMobile);
      })();
    }

    registerLocaleData(es);
    if (this.platformService.isBrowser()) {
      if (this.isInAndroidWebView() || this.isInIOSWebView()) {
        this.isInIframe = true;
        iframeService.setInIframe(true);
      } else {
        this.isInIframe = false;
        iframeService.setInIframe(false);
      }
    }

    if (this.platformService.isBrowser()) {
      if (this.constantesGlobales.codigoAdsense) {
        this.setUpAdsense(this.constantesGlobales.codigoAdsense);
      }
    }

    if (this.router?.events) {
      this.navigationSub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        if (!this.consents.init) {
          this.checkRutaCookies();
        }
        if (!this.isShow) {
          this.isShow = true;
        }
        this.seoService.insertaScriptBreadcrumb();
      });
    }

    this.setFooterVisibility();
  }

  /**
   * Método del ciclo de vida de Angular que se llama cuando el componente se destruye.
   * Aquí se desuscriben todas las suscripciones para evitar fugas de memoria.
   *
   * - `this.navigationSub`: Suscripción a la navegación.
   * - `this.titleSub`: Suscripción al título.
   * - `this.consentsSub`: Suscripción a los consentimientos.
   * - `this.publicidadSubscribe`: Suscripción a la publicidad.
   */
  ngOnDestroy(): void {
    if (this.navigationSub) this.navigationSub.unsubscribe();
    if (this.titleSub) this.titleSub.unsubscribe();
    if (this.consentsSub) this.consentsSub.unsubscribe();
    if (this.publicidadSubscribe) this.publicidadSubscribe.unsubscribe();
    if (this.routerSub) this.routerSub.unsubscribe();
  }

  /**
   * Método de ciclo de vida de Angular que se ejecuta al inicializar el componente.
   *
   * - Obtiene la configuración de prensa y ajusta propiedades de estilo basadas en la configuración.
   * - Obtiene los canales de noticias y los establece en el servicio de noticias.
   * - Obtiene los videos más vistos y los establece en el servicio de noticias.
   * - Obtiene las últimas noticias y establece el total en el servicio de noticias.
   * - Actualiza las URLs canónicas.
   * - Obtiene la publicidad de Ispot.
   * - Escucha eventos de navegación y ajusta la propiedad `pageReels` según la URL.
   */
  ngOnInit() {
    // LOGS DE PRUEBA
    // console.log("TRIBUNA-221 Actualizacion Angular 19 1.0")
    const idAmbito = this.constantesGlobales.idAmbito;
    this.initConfigPrensa();
    this.portadaService.getCanalesApi(idAmbito);
    this.loadVideosMasVistos();
    this.loadUltimasNoticias();
    this.updateMetaTags();
    this.canonicalService.setCanonicalUrl();
    this.seoService.ngOnInit();
    this.subscribeToNavigationEvents();
  }

  /**
   * Actualiza las metaetiquetas del documento HTML y configura el enlace del feed RSS.
   *
   * - Genera un arreglo de metaetiquetas basado en las constantes globales de la aplicación.
   * - Actualiza las URLs canónicas utilizando el servicio `canonicalService`.
   * - Si la plataforma es un navegador, configura el atributo `href` del enlace RSS.
   *
   * @private
   * @remarks
   * Este método utiliza las constantes globales para establecer valores dinámicos en las metaetiquetas,
   * como el título del sitio, la descripción, la URL, y otros metadatos relevantes para SEO y redes sociales.
   *
   * @throws {Error} Puede lanzar un error si no se encuentra el elemento RSS en el DOM.
   */
  private updateMetaTags() {
    const metaTagsArray: MetaDefinition[] = [
      { name: "description", content: this.constantesGlobales.metaTag },
      { property: "og:title", content: this.constantesGlobales.siteTitle },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${this.constantesGlobales.siteDomain}/assets/images/${this.constantesGlobales.logoMain}` },
      { property: "og:url", content: this.constantesGlobales.siteDomain },
      { property: "og:description", contenºt: this.constantesGlobales.metaTag },
      { property: "article:author", content: this.constantesGlobales.siteTitle },
      { property: "article:publisher", content: this.constantesGlobales.siteTitle },
      { property: "og:site_name", content: this.constantesGlobales.siteDomain },
      { name: "DC.title", content: this.constantesGlobales.siteTitle },
      { name: "DC.creator", content: this.constantesGlobales.siteTitle },
      { name: "DC.publisher", content: this.constantesGlobales.siteTitle },
      { name: "DC.description", content: this.constantesGlobales.metaTag },
      { name: "DC.subject", content: this.constantesGlobales.metaTag },
      { name: "DC.date", content: new Date().toISOString() },
    ];
    this.canonicalService.updateCanonicalURls(metaTagsArray);

    // Actualiza el título del documento.
    this.titleService.setTitle(this.constantesGlobales.siteTitle);

    if (this.platformService.isBrowser()) {
      this.document.querySelector('[type="application/rss+xml"]').setAttribute("href", this.constantesGlobales.siteDomain + "/rssfeed");
    }
  }

  private initConfigPrensa(): void {
    const configPrensa = this.configService.getConfigPrensa();
    if (configPrensa) {
      this.handleConfigPrensa(configPrensa);
    }
  }

  private async handleConfigPrensa(configPrensa: Observable<any>): Promise<void> {
    const result = await firstValueFrom(configPrensa);
    this.flotante_footer = result.general["global-constants"].flotante_footer === "true";
    this.setRoundBorder(result.general["global-constants"]);
  }

  private setRoundBorder(constants: any): void {
    if (constants.round_border === "true" && constants.round_border_radius) {
      this.elementRef.nativeElement.style.setProperty("--round-noticias", `${constants.round_border_radius}px`);
    }
  }

  private loadVideosMasVistos(): void {
    const idAmbito = this.constantesGlobales.idAmbito;
    const mostWatchVideos = this.noticiasService.getVideosMasVistosApi(10, idAmbito);
    if (mostWatchVideos) {
      this.handleVideosMasVistos(mostWatchVideos);
    }
  }

  private async handleVideosMasVistos(videos: Observable<any>): Promise<void> {
    const result = await firstValueFrom(videos);
    this.noticiasService.setVideosMasVistos(result);
  }

  private loadUltimasNoticias(): void {
    const idAmbito = this.constantesGlobales.idAmbito;
    const lastNews = this.noticiasService.getUltimasNoticias(10, null, null, idAmbito);
    if (lastNews) {
      this.handleUltimasNoticias(lastNews);
    }
  }

  private async handleUltimasNoticias(noticias: Observable<any>): Promise<void> {
    const data = await firstValueFrom(noticias);
    this.noticiasService.setTotalUltimasNoticias(data);
  }

  private subscribeToNavigationEvents(): void {
    if (this.router?.events) {
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
        this.pageReels = event.url.includes("/reels");
      });
    }
  }

  requestPushNotifications() {
    if (Notification.permission === "default") {
      // Si el permiso es "default", significa que aún no se ha solicitado
      this.firebaseService.requestPermissionAndSubscribe("noticias");
    } else if (Notification.permission === "granted") {
      console.log("Permiso de notificaciones ya concedido");
      // Aquí puedes continuar con la lógica que necesite el permiso
    } else {
      console.warn("Permiso de notificaciones denegado");
      // Aquí puedes ofrecer al usuario una forma de cambiar la configuración manualmente si lo deseas
    }
  }

  /**
   * Suscribirse a las notificaciones push
   */
  // subscribeToPush(): void {
  //   // Usamos el observable `swPush.notification` para recibir mensajes
  //   this.swPush.messages.subscribe({
  //     next: (message: any) => {
  //       console.log(message, "Message to show in the notification");
  //       // Aquí podrías mostrar la notificación o procesar el mensaje como prefieras
  //     },
  //     error: (error: any) => {
  //       console.error("Error al recibir el mensaje push", error);
  //       // Puedes manejar el error de suscripción a push aquí
  //     },
  //     complete: () => {
  //       console.log("Suscripción a notificaciones completada");
  //       // Opcional: Aquí puedes realizar acciones adicionales cuando se complete la suscripción
  //     },
  //   });
  // }

  /**
   * Configura el script de Adsense con el ID de analítica proporcionado.
   *
   * @param idAnalytic - El ID de analítica que se utilizará para configurar el script de Adsense.
   * @returns void
   */
  setUpAdsense(idAnalytic): void {
    const scriptElement = this.renderer2.createElement("script");
    scriptElement.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=" + idAnalytic;
    scriptElement.crossOrigin = "anonymous";
  }

  /**
   * Establece la visibilidad del pie de página.
   *
   * Este método suscribe al observable `getShowFooter` del servicio `footerService`
   * y actualiza la propiedad `showFooter` con el valor emitido por el observable.
   *
   * @returns {void}
   */
  setFooterVisibility(): void {
    this.footerService.getShowFooter().subscribe((isShow) => {
      this.showFooter = isShow;
    });
  }
  /**
   * Obtiene y asigna la publicidad para diferentes posiciones en la aplicación.
   *
   * Este método suscribe a un servicio de publicidad para obtener un conjunto de imágenes publicitarias.
   * Filtra las imágenes según su posición y asigna una imagen aleatoria a cada posición específica.
   * Además, gestiona la visualización de publicidad intersticial y su temporizador de cierre.
   *
   * @returns {void}
   */
  getPublicidadIspot(): void {
    this.publicidadSubscribe = this.publicidadService.getPubli().subscribe((result) => {
      const publicidadFondoCompleto = result.imagenes.filter((item) => item.idPosicion === "53");
      let numPubli = Math.floor(Math.random() * publicidadFondoCompleto.length);
      this.publicidadFondoCompleto = publicidadFondoCompleto[numPubli];
      if (!this.publicidadFondoCompleto) {
        const arrayPublicidadCabecera = result.imagenes.filter((item) => item.idPosicion === "1");
        numPubli = Math.floor(Math.random() * arrayPublicidadCabecera.length);
        this.publicidadCabecera = arrayPublicidadCabecera[numPubli];

        const arrayPublicidadIzquierda = result.imagenes.filter((item) => item.idPosicion === "17"); // lateral abajo
        numPubli = Math.floor(Math.random() * arrayPublicidadIzquierda.length);
        this.publicidadIzquierda = arrayPublicidadIzquierda[numPubli];

        const arrayPublicidadDerecha = result.imagenes.filter((item) => item.idPosicion === "2");
        numPubli = Math.floor(Math.random() * arrayPublicidadDerecha.length);
        this.publicidadDerecha = arrayPublicidadDerecha[numPubli];

        const arrayPublicidadIzquierdaAbajo = result.imagenes.filter((item) => item.idPosicion === "26"); // lateral abajo
        numPubli = Math.floor(Math.random() * arrayPublicidadIzquierdaAbajo.length);
        this.publicidadIzquierdaAbajo = arrayPublicidadIzquierdaAbajo[numPubli];

        const arrayPublicidadDerechaAbajo = result.imagenes.filter((item) => item.idPosicion === "25");
        numPubli = Math.floor(Math.random() * arrayPublicidadDerechaAbajo.length);
        this.publicidadDerechaAbajo = arrayPublicidadDerechaAbajo[numPubli];
      }

      const arrayPublicidadFooter = result.imagenes.filter((item) => item.idPosicion == 5); // footer
      numPubli = Math.floor(Math.random() * arrayPublicidadFooter.length);
      this.publicidadFooter = arrayPublicidadFooter[numPubli];

      const arrayPublicidadFooterMovil = result.imagenes.filter((item) => item.idPosicion === "32"); // footer movil
      numPubli = Math.floor(Math.random() * arrayPublicidadFooterMovil.length);
      this.publicidadFooterMovil = arrayPublicidadFooterMovil[numPubli];

      const arrayPublicidadInstertsticial = result.imagenes.filter((item) => item.idPosicion == 54);
      numPubli = Math.floor(Math.random() * arrayPublicidadInstertsticial.length);
      this.publicidadInstertsticial = arrayPublicidadInstertsticial[numPubli];

      const arrayPublicidadInstertsticialMovil = result.imagenes.filter((item) => item.idPosicion == 30);
      numPubli = Math.floor(Math.random() * arrayPublicidadInstertsticialMovil.length);
      this.publicidadInstertsticialMovil = arrayPublicidadInstertsticialMovil[numPubli];

      if (this.publicidadInstertsticial || this.publicidadInstertsticialMovil) {
        this.mostrarPublicidadIntersticial = true;
        this.mostrarPublicidadIntersticialMovil = true;
        this.temporizadorCierreIntersticial = 5;
        let interval;
        if (this.platformService.isBrowser()) {
          interval = setInterval(() => {
            this.temporizadorCierreIntersticial -= 1;
            if (this.temporizadorCierreIntersticial == 0) {
              this.mostrarPublicidadIntersticial = false;
              this.mostrarPublicidadIntersticialMovil = false;
              clearInterval(interval);
            }
          }, 1000);
        }
        this.router.events.subscribe((event) => {
          if (event instanceof NavigationEnd) {
            this.mostrarPublicidadIntersticial = true;
            this.mostrarPublicidadIntersticialMovil = true;
            this.temporizadorCierreIntersticial = 5;
            if (this.platformService.isBrowser()) {
              interval = setInterval(() => {
                this.temporizadorCierreIntersticial -= 1;
                if (this.temporizadorCierreIntersticial == 0) {
                  this.mostrarPublicidadIntersticial = false;
                  this.mostrarPublicidadIntersticialMovil = false;
                  clearInterval(interval);
                }
              }, 1000);
            }
          }
        });
      }
    });
  }

  /**
   * Método del ciclo de vida de Angular que se llama después de que la vista del componente ha sido inicializada.
   *
   * Este método suscribe al observable `consentType` del servicio `consentService` y realiza diferentes acciones
   * basadas en el tipo de consentimiento recibido. Si el tipo de consentimiento es `false`, inicializa la publicidad.
   *
   * Si el tipo de consentimiento no es `false`, se suscribe al observable `consents` del servicio `consentService`
   * y actualiza los consentimientos. Si los consentimientos no están inicializados y la plataforma es un navegador,
   * establece el estilo `overflow: hidden` en el cuerpo del documento.
   *
   * @returns {void}
   */
  ngAfterViewInit(): void {
    if (this.consentService.consentType) {
      (async () => {
        const consentType = (await firstValueFrom(this.consentService.consentType)) as consentType;
        this.consentType = consentType;
        if (this.platformService.isBrowser()) {
          if (consentType === false) {
            this.refrescoPublicidad();
            // this.consentService.initPublicidad();
            // this.consentService.initRendimiento();
            return;
          }

          this.consentsSub = this.consentService.consents.subscribe((consents: consents) => {
            this.consents = consents;
            if (this.platformService.isBrowser() && consents.init === false) {
              this.renderer2.setStyle(this.document.body, "overflow", "hidden");
            }
          });
        }
      })();
    }

    if (this.platformService.isBrowser() && environment.production) {
      setTimeout(() => {
        const metrics = this.performanceTrackingService.getPerformanceMetrics();
        console.log("metrics", metrics);
        this.performanceTrackingService.sendPerformanceMetrics(metrics);
      }, 1000);
    }
  }

  /**
   * Detiene la síntesis de texto a voz (TTS) si se está ejecutando en un navegador.
   *
   * @remarks
   * Esta función verifica si la aplicación se está ejecutando en un entorno de navegador
   * y si la API de síntesis de voz del navegador está disponible. Si ambas condiciones
   * se cumplen, cancela cualquier síntesis de voz en curso.
   *
   * @returns {void} No retorna ningún valor.
   */
  stopTTS(): void {
    if (this.platformService.isBrowser()) {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    }
  }

  /**
   * Oculta la característica estableciendo la propiedad `isShow` a `false`.
   *
   * @returns {void}
   */
  featureHide(): void {
    this.isShow = false;
  }

  /**
   * Cierra la publicidad intersticial tanto en la versión de escritorio como en la versión móvil.
   * Establece las propiedades `mostrarPublicidadIntersticial` y `mostrarPublicidadIntersticialMovil` a `false`.
   */
  cerrarPublicidadIntersticial(): void {
    this.mostrarPublicidadIntersticial = false;
    this.mostrarPublicidadIntersticialMovil = false;
  }

  /**
   * Acepta los datos del evento y actualiza las configuraciones de consentimiento.
   *
   * @param eventData - Datos del evento opcionales que contienen las configuraciones de consentimiento.
   *
   * Si `eventData` está presente:
   * - Si `eventData` no tiene cambios y `this.consents.config` está configurado, desactiva la configuración de consentimiento.
   * - Actualiza las configuraciones de publicidad y rendimiento basadas en `eventData`.
   * - Si las configuraciones previas de publicidad o rendimiento estaban activadas y ahora están desactivadas, recarga la página.
   *
   * Si `eventData` no está presente:
   * - Activa las configuraciones de publicidad y rendimiento por defecto.
   *
   * Finalmente, guarda las configuraciones de consentimiento en una cookie.
   */
  accept(eventData?: any) {
    if (eventData) {
      if (!eventData.changes && this.consents.config) {
        this.consentService.setConfig(false);
        if (this.platformService.isBrowser()) {
          this.renderer2.setStyle(this.document.body, "overflow", "auto");
        }
        return;
      }
      const prePublicidad = this.consentService.prePubli;
      const preRendimiento = this.consentService.preAnalytics;
      this.consentService.setPublicidad(eventData.publicidad);
      this.consentService.setRendimiento(eventData.rendimiento);
      if ((prePublicidad && !eventData.publicidad) || (preRendimiento && !eventData.rendimiento)) {
        if (this.platformService.isBrowser()) {
          window.location.reload();
        }
      }
    } else {
      this.consentService.setPublicidad(true);
      this.consentService.setRendimiento(true);
    }
    this.consentService.saveCookie();
  }

  /**
   * La función guarda una cookie con los consentimientos rechazados.
   */
  reject() {
    this.consentService.saveCookie();
  }

  /**
   * La función reconfigura las cookies estableciendo la configuración de consentimiento en verdadero y
   * ocultando el desbordamiento del cuerpo.
   */
  reconfigureCookies() {
    this.consentService.setConfig(true);
    if (this.platformService.isBrowser()) {
      this.renderer2.setStyle(this.document.body, "overflow", "hidden");
    }
  }

  /**
   * La función comprueba si la ruta actual es '/politica-cookies' y establece la variable
   * 'mostrarConsent' en consecuencia.
   * @returns Si la ruta actual es '/politica-cookies', la función no devolverá nada. De lo contrario,
   * devolverá verdadero.
   */
  checkRutaCookies() {
    if (this.platformService.isBrowser()) {
      if (this.router.url === "/politica-cookies") {
        this.renderer2.setStyle(this.document.body, "overflow", "auto");
        this.mostrarConsent = false;
        return;
      }

      this.mostrarConsent = true;
    }
  }

  /**
   * Maneja el evento de desplazamiento de la ventana.
   * Calcula el porcentaje de desplazamiento de la página y envía un evento a Google Analytics
   * cuando se alcanza el 10% de desplazamiento.
   *
   * @remarks
   * - Utiliza `window.pageYOffset` o `document.documentElement.scrollTop` para obtener la posición de desplazamiento vertical.
   * - Calcula la altura del documento restando la altura de la ventana de la altura total del documento.
   * - Evita divisiones por cero si la página es muy corta.
   * - Envía un evento a Google Analytics usando `ngx-google-analytics` cuando se alcanza el 10% de desplazamiento.
   *
   * @returns {void}
   */
  @HostListener("window:scroll", ["$event"])
  onWindowScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) {
      // Evita divisiones por cero  si la página es muy corta
      return;
    }

    const scrollPercent = (scrollTop / docHeight) * 100;

    if (!this.scrollEventFired && scrollPercent >= 1) {
      this.scrollEventFired = true;
      // Envía evento a Google Analytics con ngx-google-analytics
      this.gaService.event("scrollDepth", "scroll", "1% Alcanzado");
    }
  }

  /**
   * Método que se suscribe a los eventos de navegación del router y refresca los anuncios publicitarios
   * cuando se detecta una navegación finalizada.
   *
   * @remarks
   * Este método verifica si la plataforma es un navegador y si el objeto `googletag` está disponible
   * y tiene anuncios publicitarios configurados. Si es así, utiliza el comando `googletag.cmd.push`
   * para refrescar los anuncios publicitarios.
   *
   * @returns {void}
   */
  refrescoPublicidad() {
    if (this.router?.events) {
      this.routerSub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        if (this.platformService.isBrowser()) {
          if (window.hasOwnProperty("googletag") && window["googletag"].pubads) {
            window["googletag"].cmd.push(() => {
              window["googletag"].pubads().refresh();
            });
          }
        }
      });
    }
  }

  /**
   * Determines whether the right sky advertisement should be rendered.
   *
   * @returns {boolean} `true` if the application is not running inside an iframe
   * and either `publicidadDerecha` or `publicidadDerechaAbajo` is true; otherwise, `false`.
   */
  get shouldRenderRightSky(): boolean {
    return !this.isInIframe && (this.publicidadDerecha || this.publicidadDerechaAbajo);
  }

  isInAndroidWebView(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /; wv/.test(userAgent);
    }
  }
  isInIOSWebView(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const userAgent = window.navigator.userAgent.toLowerCase();

      const isIOS = /iphone|ipod|ipad/.test(userAgent);
      const notSafari = !/safari/.test(userAgent);
      const notStandalone = !("standalone" in window.navigator && window.navigator["standalone"]);

      return isIOS && notSafari && notStandalone;
    }
  }
}
